import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, Download, FileText, MessageSquare, Loader2, Trash2, User, MapPin, Users, BookOpen, Cloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api, API, STATUS_META, DOC_META, fileUrl } from "@/lib/api";
import DocumentVerificationModal from "@/components/DocumentVerificationModal";
import KelulusanCard from "@/components/KelulusanCard";
import DownloadPanel from "@/components/DownloadPanel";
import ManualGradeEditor from "@/components/ManualGradeEditor";

export default function StudentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [verifyModal, setVerifyModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [drivesyncing, setDriveSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/students/${id}`);
      setStudent(data);
      setNote(data.field_notes?.__global__ || "");
    } catch (err) {
      toast.error("Siswa tidak ditemukan");
      nav("/admin");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const verifyStudent = async (valid) => {
    try {
      await api.put(`/admin/students/${id}/verify`, { valid, note });
      toast.success(valid ? "Siswa telah diverifikasi" : "Permintaan perbaikan dikirim");
      load();
    } catch (err) {
      toast.error("Gagal");
    }
  };

  const deleteStudent = async () => {
    if (!window.confirm("Hapus data siswa ini? Tindakan ini tidak dapat dibatalkan.")) return;
    try {
      await api.delete(`/admin/students/${id}`);
      toast.success("Data siswa dihapus");
      nav("/admin");
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/students/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Buku_Induk_${student.nisn}_${(student.nama_lengkap || "siswa").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF berhasil diunduh");
    } catch {
      toast.error("Gagal mengunduh PDF");
    } finally {
      setDownloading(false);
    }
  };

  const previewPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/students/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Gagal membuka pratinjau");
    } finally {
      setDownloading(false);
    }
  };

  const syncToDrive = async () => {
    setDriveSyncing(true);
    try {
      const { data } = await api.post(`/admin/drive/sync/${id}`);
      toast.success(`Berhasil sinkronkan ${data.synced} berkas ke Google Drive`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal sinkron ke Drive");
    } finally {
      setDriveSyncing(false);
    }
  };

  if (loading || !student) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  const meta = STATUS_META[student.status] || STATUS_META.draft;
  const docs = student.documents || {};
  const completeDocs = Object.values(docs).filter((d) => d && d.status === "verified").length;
  const totalDocs = Object.keys(DOC_META).length;

  return (
    <div className="min-h-screen bg-slate-50/70" data-testid="student-detail-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900 text-sm" data-testid="back-to-dashboard-link">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="text-center">
            <div className="font-display font-bold text-slate-900 text-sm">{student.nama_lengkap || "Siswa Baru"}</div>
            <div className="text-xs text-slate-500 font-mono">NISN: {student.nisn}</div>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${meta.cls}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Action Bar */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 p-4 bg-white rounded-xl border border-slate-200">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status Verifikasi</div>
            <div className="mt-1 text-sm text-slate-700">
              Dokumen terverifikasi: <strong>{completeDocs}/{totalDocs}</strong>
              {student.kelas && <span className="ml-3">Kelas: <strong>{student.kelas}</strong></span>}
            </div>
          </div>
          <Button variant="outline" onClick={() => setVerifyModal(true)} className="h-10" data-testid="open-verification-modal-button">
            <FileText className="w-4 h-4 mr-1.5" /> Verifikasi Dokumen
          </Button>
          <Button variant="outline" onClick={syncToDrive} disabled={drivesyncing} className="h-10 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="sync-drive-button">
            {drivesyncing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Cloud className="w-4 h-4 mr-1.5" />}
            Sinkron ke Drive
          </Button>
        </div>

        <DownloadPanel student={student} />

        <ManualGradeEditor student={student} onUpdated={load} />

        <Tabs defaultValue="biodata">
          <TabsList className="bg-slate-100 h-auto p-1 rounded-xl">
            <TabsTrigger value="biodata" className="py-2 px-4" data-testid="tab-biodata"><User className="w-4 h-4 mr-1.5" />Biodata</TabsTrigger>
            <TabsTrigger value="alamat" className="py-2 px-4" data-testid="tab-alamat-detail"><MapPin className="w-4 h-4 mr-1.5" />Alamat</TabsTrigger>
            <TabsTrigger value="ortu" className="py-2 px-4" data-testid="tab-ortu-detail"><Users className="w-4 h-4 mr-1.5" />Orang Tua</TabsTrigger>
            <TabsTrigger value="nilai" className="py-2 px-4" data-testid="tab-nilai"><BookOpen className="w-4 h-4 mr-1.5" />Nilai</TabsTrigger>
          </TabsList>

          <TabsContent value="biodata" className="mt-6">
            <Card>
              <CardHeader><CardTitle className="font-display text-lg">Identitas Peserta Didik</CardTitle></CardHeader>
              <CardContent>
                <InfoGrid rows={[
                  ["Nama Lengkap", student.nama_lengkap], ["Nama Panggilan", student.nama_panggilan],
                  ["NISN", student.nisn], ["NIS", student.nis], ["NIK", student.nik],
                  ["Jenis Kelamin", student.jenis_kelamin], ["Tempat Lahir", student.tempat_lahir],
                  ["Tanggal Lahir", student.tanggal_lahir], ["Agama", student.agama],
                  ["Kewarganegaraan", student.kewarganegaraan], ["Anak ke-", student.anak_ke],
                  ["Jumlah Saudara", student.jumlah_saudara], ["Golongan Darah", student.golongan_darah],
                  ["Tinggi Badan", student.tinggi_badan && `${student.tinggi_badan} cm`],
                  ["Berat Badan", student.berat_badan && `${student.berat_badan} kg`],
                ]} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="alamat" className="mt-6 space-y-6">
            <Card>
              <CardHeader><CardTitle className="font-display text-lg">Alamat Tempat Tinggal</CardTitle></CardHeader>
              <CardContent>
                <InfoGrid rows={[
                  ["Alamat", student.alamat], ["Kelurahan/Desa", student.kelurahan],
                  ["Kecamatan", student.kecamatan], ["Kabupaten/Kota", student.kabupaten],
                  ["Provinsi", student.provinsi], ["Kode Pos", student.kode_pos],
                  ["Nomor HP", student.nomor_hp], ["Tinggal Bersama", student.tinggal_bersama],
                  ["Jarak ke Sekolah", student.jarak_sekolah],
                ]} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="font-display text-lg">Pendidikan Sebelumnya</CardTitle></CardHeader>
              <CardContent>
                <InfoGrid rows={[
                  ["Asal Sekolah", student.asal_sekolah], ["NPSN Asal", student.npsn_asal],
                  ["Nomor Ijazah", student.nomor_ijazah], ["Tahun Ijazah", student.tahun_ijazah],
                  ["Nomor SKHUN", student.nomor_skhun], ["Kelas", student.kelas],
                  ["Tanggal Diterima", student.tanggal_diterima],
                ]} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ortu" className="mt-6 space-y-6">
            <ParentInfo title="Data Ayah Kandung" data={student.ayah} />
            <ParentInfo title="Data Ibu Kandung" data={student.ibu} />
            {student.wali?.nama && <ParentInfo title="Data Wali" data={student.wali} />}
          </TabsContent>

          <TabsContent value="nilai" className="mt-6 space-y-6">
            <Card>
              <CardHeader><CardTitle className="font-display text-lg">Nilai Semester (Leger / Input Manual)</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {[1, 2, 3, 4, 5, 6].map((sem) => {
                  const sd = student.semesters?.[String(sem)] || {};
                  const grades = sd.grades || [];
                  if (grades.length === 0) return (
                    <div key={sem} className="text-sm text-slate-500 p-3 border border-dashed border-slate-200 rounded-lg" data-testid={`sem-${sem}-empty`}>
                      Semester {sem}: Belum ada data
                    </div>
                  );
                  return (
                    <div key={sem} data-testid={`sem-${sem}-block`}>
                      <div className="text-sm font-semibold text-slate-900 mb-2 font-display">Semester {sem}</div>
                      <div className="overflow-x-auto border border-slate-200 rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-slate-700">Mata Pelajaran</th>
                              <th className="text-center px-3 py-2 font-medium text-slate-700 w-16">KKM</th>
                              <th className="text-center px-3 py-2 font-medium text-slate-700 w-16">Nilai</th>
                              <th className="text-center px-3 py-2 font-medium text-slate-700 w-20">Predikat</th>
                            </tr>
                          </thead>
                          <tbody>
                            {grades.map((g, i) => (
                              <tr key={i} className="border-t border-slate-200">
                                <td className="px-3 py-2 text-slate-900">{g.mata_pelajaran}</td>
                                <td className="px-3 py-2 text-center text-slate-600">{g.kkm}</td>
                                <td className="px-3 py-2 text-center font-semibold">{g.nilai}</td>
                                <td className="px-3 py-2 text-center text-slate-600">{g.predikat}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {sd.attendance && (
                        <div className="mt-2 text-xs text-slate-600 flex gap-4">
                          <span>Sakit: <strong>{sd.attendance.sakit ?? 0}</strong></span>
                          <span>Izin: <strong>{sd.attendance.izin ?? 0}</strong></span>
                          <span>Tanpa Keterangan: <strong>{sd.attendance.alpa ?? 0}</strong></span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
            {student.ekstrakurikuler?.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="font-display text-lg">Ekstrakurikuler</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {student.ekstrakurikuler.map((e, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm">
                        <span className="w-2 h-2 rounded-full bg-blue-600" />
                        <span className="font-medium text-slate-900">{e.nama}</span>
                        {e.keterangan && <span className="text-slate-500">— {e.keterangan}</span>}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        <KelulusanCard student={student} onUpdated={load} />

        {/* Verification Action */}
        <Card>
          <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><MessageSquare className="w-5 h-5" /> Aksi Verifikasi Data</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Catatan untuk siswa (opsional, wajib jika minta perbaikan)..."
              data-testid="global-note-input"
            />
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => verifyStudent(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="validate-verify-button">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Validasi & Verifikasi
              </Button>
              <Button onClick={() => verifyStudent(false)} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="request-correction-button">
                <XCircle className="w-4 h-4 mr-1.5" /> Minta Perbaikan
              </Button>
              <Button onClick={deleteStudent} variant="ghost" className="text-rose-600 hover:bg-rose-50 ml-auto" data-testid="delete-student-button">
                <Trash2 className="w-4 h-4 mr-1.5" /> Hapus
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <DocumentVerificationModal
        open={verifyModal}
        onOpenChange={setVerifyModal}
        student={student}
        onUpdated={load}
      />
    </div>
  );
}

const InfoGrid = ({ rows }) => (
  <dl className="grid md:grid-cols-2 gap-x-8 gap-y-3">
    {rows.map(([k, v]) => (
      <div key={k} className="flex items-baseline gap-3">
        <dt className="text-xs uppercase tracking-wider text-slate-500 font-medium min-w-[140px]">{k}</dt>
        <dd className="text-sm text-slate-900">{v || <span className="text-slate-400 italic">—</span>}</dd>
      </div>
    ))}
  </dl>
);

const ParentInfo = ({ title, data }) => {
  const d = data || {};
  return (
    <Card>
      <CardHeader><CardTitle className="font-display text-lg">{title}</CardTitle></CardHeader>
      <CardContent>
        <InfoGrid rows={[
          ["Nama", d.nama], ["NIK", d.nik], ["Tempat Lahir", d.tempat_lahir],
          ["Tanggal Lahir", d.tanggal_lahir], ["Agama", d.agama], ["Pendidikan", d.pendidikan],
          ["Pekerjaan", d.pekerjaan], ["Penghasilan", d.penghasilan], ["Nomor HP", d.nomor_hp],
          ["Hubungan", d.hubungan], ["Alamat", d.alamat],
        ].filter((r) => r[0] !== "Hubungan" || d.hubungan)} />
      </CardContent>
    </Card>
  );
};
